import { useQuery } from '@tanstack/react-query';

import { fetchCompanyByRcid } from '../api/fetchCompanyByRcid';
import type { CompanySearchApiResult } from '../api/fetchCompanySearchResults';
import type { EntityInfoData } from '../components/tree/EntityInfoPopover';

/** Map a /get-company result to the entity-info the hover popover renders. */
const toEntityInfo = (company: CompanySearchApiResult): EntityInfoData => {
  const ids = company.primary_identifiers;
  return {
    rcid: company.rcid,
    ult_parent_name: company.ult_parent_name,
    ult_parent_rcid: company.ult_parent_rcid,
    hasUltimateParent: company.ult_parent_rcid !== company.rcid,
    primary_website: ids?.primary_website,
    primary_linkedin_url: ids?.primary_linkedin_url,
    industry: ids?.rics_k400_name,
    city: ids?.city,
    state: ids?.state,
    country: ids?.country,
    rics_k400: ids?.rics_k400,
  };
};

/**
 * Fetch a company's full tooltip detail by rcid (via /get-company) for browse
 * trees whose nodes carry only an id (no `entityInfo`) — e.g. the company
 * placeholder tree. react-query caches per rcid (so re-hovering never refetches)
 * and company detail is static within a session (`staleTime: Infinity`).
 * Disabled callers (search-mode nodes that already have `entityInfo`) never
 * fetch; a failed lookup is swallowed via `skipGlobalErrorHandling` so the
 * tooltip just shows less detail instead of popping the global error dialog.
 */
export const useCompanyEntityInfo = (
  rcid: number | null,
  { enabled = true }: { enabled?: boolean } = {}
): {
  entityInfo: EntityInfoData | undefined;
  isLoading: boolean;
  /** Settled — the lookup ran and came back, with a company or without one. */
  isFetched: boolean;
} => {
  const { data, isLoading, isFetched } = useQuery({
    queryKey: ['company-entity-info', rcid],
    queryFn: async ({ signal }) => {
      const company = await fetchCompanyByRcid(rcid as number, signal);
      return company ? toEntityInfo(company) : null;
    },
    enabled: enabled && rcid != null,
    staleTime: Number.POSITIVE_INFINITY,
    meta: { skipGlobalErrorHandling: true },
  });

  return { entityInfo: data ?? undefined, isLoading, isFetched };
};
